import React from "react";
import { useSearchParams } from "react-router-dom";

import Topbar from "../../components/topbar";
import type { Column } from '../../components/admin-table';
import { Box, Button, Chip, ToggleButton, ToggleButtonGroup } from "@mui/material";
import DynamicAdminTable from "../../components/admin-table";
import Pagination from "../../components/pagination";
import { useToast } from "../../providers/toaster-provider";

import useApi from "../../hooks/use-api";
import buildApiRequestConfig from "../../api/url-builder";
import routes from "../../api/routes";

const PAGE_SIZE = 50;

// Presets (activity-log-controller.js ACTIVITY_CATEGORIES) - Auto-Provisions
// used to be its own page; it's the auto_provision preset now.
const PRESETS = [
    { key: 'all', label: 'All' },
    { key: 'login', label: 'Logins' },
    { key: 'page_visit', label: 'Page Visits' },
    { key: 'impersonate', label: 'Impersonation' },
    { key: 'provision_aging', label: 'Provision Aging' },
    { key: 'auto_provision', label: 'Auto-Provisions' },
    { key: 'other', label: 'Other' },
] as const;
type PresetKey = typeof PRESETS[number]['key'];

type ActivityLog = {
    id: number;
    entityId: number;
    userGuid: string | null;
    userEmail: string | null;
    type: string;
    module: string;
    description: string;
    createdAt: string;
    status?: string;
    elapsedTime?: string;
    entity?: { id: number; name: string } | null;
};

const STATUS_COLORS: Record<string, 'success' | 'error' | 'warning' | 'info' | 'default'> = {
    completed: 'success',
    cancelled: 'warning',
    failed: 'error',
    timeout: 'error',
    pending: 'info',
    'in-progress': 'info',
};
const isRunning = (status?: string) => status === 'pending' || status === 'in-progress';

const ActivityLogs: React.FC = () => {
    const { showError, showSuccess } = useToast();
    const [searchParams, setSearchParams] = useSearchParams();
    const presetParam = searchParams.get('preset') as PresetKey | null;
    const preset: PresetKey = PRESETS.some(p => p.key === presetParam) ? (presetParam as PresetKey) : 'all';
    const [data, setData] = React.useState<ActivityLog[]>([]);
    const [counts, setCounts] = React.useState<Record<string, number>>({});
    const [totalPages, setTotalPages] = React.useState(1);
    const [totalItems, setTotalItems] = React.useState(0);
    const [page, setPage] = React.useState(1);

    const [fetch, setFetch] = useApi();
    const [cancel, setCancel] = useApi();

    const load = React.useCallback(() => {
        const queryParams = [
            { param: 'page', value: page.toString() },
            { param: 'pageSize', value: PAGE_SIZE.toString() },
            { param: 'sortBy', value: 'createdAt' },
            { param: 'sortOrder', value: 'DESC' },
            ...(preset !== 'all' ? [{ param: 'category', value: preset }] : []),
            { param: 'isActive', value: 'true' },
        ];
        const { url, method } = buildApiRequestConfig(routes.activityLogs.fetchAll, queryParams);
        setFetch({ url, method });
    }, [page, preset, setFetch]);

    React.useEffect(() => { load(); }, [load]);

    React.useEffect(() => {
        const { data: responseData, isLoading, error } = fetch;
        if (responseData && !isLoading) {
            setData(responseData?.activities || []);
            setCounts(responseData?.counts || {});
            setTotalPages(responseData?.totalPages || 1);
            setTotalItems(responseData?.total || 0);
        }
        if (error) {
            showError("Failed to fetch activity logs");
        }
    }, [fetch]); // eslint-disable-line react-hooks/exhaustive-deps

    React.useEffect(() => {
        const { data: result, isLoading, error } = cancel;
        if (result && !isLoading) {
            showSuccess('Auto-provision cancelled');
            load();
        }
        if (error) showError(typeof error === 'string' ? error : 'Failed to cancel auto-provision');
    }, [cancel]); // eslint-disable-line react-hooks/exhaustive-deps

    const choosePreset = (key: PresetKey) => {
        setPage(1);
        setSearchParams(key === 'all' ? {} : { preset: key }, { replace: true });
    };

    const handleCancel = (row: ActivityLog) => {
        const { url, method } = buildApiRequestConfig(routes.activityLogs.cancelAutoProvision, [{ param: 'entityId', value: row.entityId }]);
        setCancel({ url, method });
    };

    const showAutoProvisionColumns = preset === 'auto_provision' || data.some(r => r.status);

    const columns: Column[] = [
        { id: 'createdAtText', label: 'Date & Time', size: 170, disabled: true },
        { id: 'company', label: 'Company', size: 160, disabled: true },
        { id: 'user', label: 'User', size: 200, disabled: true },
        { id: 'type', label: 'Activity', size: 130, disabled: true },
        { id: 'module', label: 'Module', size: 130, disabled: true },
        { id: 'description', label: 'Description', size: 320, disabled: true },
        ...(showAutoProvisionColumns ? [
            { id: 'statusCell', label: 'Status', size: 170, disabled: true },
        ] : []),
    ];

    const rows = data.map(row => ({
        ...row,
        createdAtText: new Date(row.createdAt).toLocaleString(),
        company: row.entity?.name || (row.entityId ? `#${row.entityId}` : '-'),
        user: row.userEmail || (row.userGuid ? row.userGuid : 'System'),
        statusCell: row.status ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip label={row.status.toUpperCase()} color={STATUS_COLORS[row.status] || 'default'} size="small" />
                <span style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{row.elapsedTime}</span>
                {isRunning(row.status) && (
                    <Button size="small" color="error" disabled={cancel.isLoading} onClick={() => handleCancel(row)} sx={{ textTransform: 'none', minWidth: 0 }}>
                        Cancel
                    </Button>
                )}
            </Box>
        ) : null,
    }));

    const countLabel = (key: PresetKey) => {
        const n = counts[key];
        if (n == null) return '';
        if (key === 'auto_provision' && counts.autoProvisionRunning) return ` (${n}) - ${counts.autoProvisionRunning} running`;
        return ` (${n})`;
    };

    return (
        <div style={{ width: '100%' }}>
            <Topbar title="Activity Logs" />
            <div style={{ padding: '10px', background: 'var(--color-page-background)', height: 'calc(100vh - 86px)' }}>
                <Box mb={1} display="flex" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap">
                    <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={preset}
                        onChange={(_, v) => v && choosePreset(v)}
                        sx={{ '& .MuiToggleButton-root': { textTransform: 'none', px: 1.75 } }}
                    >
                        {PRESETS.map(p => (
                            <ToggleButton key={p.key} value={p.key}>{p.label}{countLabel(p.key)}</ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                    <span style={{ fontSize: '14px', color: 'var(--color-text-muted)' }}>
                        Total Records: {totalItems}
                    </span>
                </Box>
                <DynamicAdminTable
                    columns={columns}
                    data={rows}
                    pagination={
                        <Pagination
                            currentPage={page}
                            totalPages={totalPages}
                            onPageChange={setPage}
                            pageSize={PAGE_SIZE}
                            totalItems={totalItems}
                        />
                    }
                />
            </div>
        </div>
    );
};

export default ActivityLogs;
